'use client';

import React, { FC, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import { Errors, DynamicComponent, Button, HeaderText } from '@/components';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { HeaderSize } from '@/components/ui/HeaderText';
import { BUTTON_STYLES } from '@/components/ui/Buttons/utils';

import { useErrorDirectives } from '@/context/ErrorDirectiveContext';
import { usePageData } from '@/context/PageDataProvider';

import { ProcessingButtonType } from '@/core/constants';
import { TypeEditChildDetailsFormFields } from '@/core/generated-types';

import { editChild } from '@/lib/api/accountManager/apis/editChild';

import { useRouter } from '@/navigation';

import { TypeEditChildPageData } from '@/pages/[locale]/account/settings/edit-child/[slug]';

import { ChildBirthDateInput } from '@/utils/dates/constants';
import { childBirthDateToDateString } from '@/utils/dates/dateFormatters';
import { validateChildBirthDate } from '@/utils/dates/validateChildBirthDate';

import {
  buildRelativeInternalUrl,
  buildQueryString,
} from '@/utils/shopSubdomain';

const EditChildDetailsForm: FC<TypeEditChildDetailsFormFields> = ({
  title,
  nameInput,
  cancelCta,
  childBirthDateInput,
  confirmCta,
}) => {
  const [processingButton, setProcessingButton] =
    useState<ProcessingButtonType>('');

  const pageData = usePageData() as TypeEditChildPageData;

  const { name, birthday, uuid: childUuid, verified_dob } = pageData.child;

  const [year, month, day] = birthday?.split('-');

  const [childName, setChildName] = useState(name || '');

  const [childBirthDate, setChildBirthDate] = useState<ChildBirthDateInput>({
    year: year || '',
    month: month || '',
    day: day || '',
  });

  const [childNameValidationError, setChildNameValidationError] = useState('');

  const [childBirthDateValidationError, setChildBirthDateValidationError] =
    useState('');

  const [serverError, setServerError] = useState(false);

  const searchParams = useSearchParams();

  const { back, push } = useRouter();

  const { errorDirectives } = useErrorDirectives();

  const { errors, heading } = findApplicableErrorDetails(errorDirectives);

  const clearServerError = () => {
    if (serverError) {
      setServerError(false);
    }
  };

  function returnToSourcePage() {
    const pageSource = searchParams?.get('page_source');

    let url = buildRelativeInternalUrl(['account', 'settings']);

    if (pageSource === 'subscriptions') {
      const queryString = buildQueryString([
        {
          key: 'updated_child_uuid',
          value: childUuid,
        },
      ]);

      url = buildRelativeInternalUrl(['account', 'subscriptions'], queryString);
    }

    push(url);
  }

  const updateChildName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChildName(event.target.value);

    if (childNameValidationError) {
      setChildNameValidationError('');
    }

    clearServerError();
  };

  const updateChildBirthDate = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    setChildBirthDate((prevState) => ({
      ...prevState,
      [type]: event.target.value,
    }));

    clearServerError();
  };

  const validateChildName = (target: any) => {
    if (nameInput.fields.required && !childName) {
      return nameInput.fields.requiredError || '';
    }

    if (target && !target.checkValidity()) {
      return nameInput.fields.validationError || '';
    }

    return '';
  };

  const submitEditChildDetailsForm = async (event: any) => {
    setProcessingButton('confirm');

    event.preventDefault();

    const freeformDateToDateString = childBirthDateToDateString(childBirthDate);

    const childBirthDateAsDate = new Date(freeformDateToDateString);

    const childBirthDateError = validateChildBirthDate(
      event,
      childBirthDateAsDate,
      childBirthDate,
      childBirthDateInput,
    );

    const childNameError = validateChildName(
      nameInput.fields.id && event.target[nameInput.fields.id],
    );

    setChildNameValidationError(childNameError);

    setChildBirthDateValidationError(childBirthDateError);

    if (childNameError || childBirthDateError) {
      setProcessingButton('');
      return;
    }

    const serverSupportedDateString = childBirthDateAsDate
      .toISOString()
      .split('T')[0];

    const res = await editChild({
      childName,
      childUuid,
      childBirthDate: serverSupportedDateString,
      userUuid: pageData.user.uuid,
      verified_dob: true,
    });

    if (res.ok) {
      returnToSourcePage();
    } else {
      setServerError(true);

      setProcessingButton('');
    }
  };

  if (!pageData.child || !pageData.user) {
    return <Errors errors={errors} heading={heading} />;
  }

  return (
    <div
      data-testid="edit-child-details-form"
      className="flex max-w-[500px] flex-col justify-center space-y-4"
    >
      {serverError && <Errors errors={errors} heading={heading} />}

      {title && <HeaderText text={title} size={HeaderSize.H1} align="left" />}

      <form onSubmit={submitEditChildDetailsForm} noValidate>
        <div className="space-y-3 pb-7">
          <DynamicComponent
            component={nameInput}
            value={childName}
            error={childNameValidationError}
            onChange={updateChildName}
          />
        </div>

        <div className="space-y-3 pb-7">
          <DynamicComponent
            component={childBirthDateInput}
            value={childBirthDate}
            error={childBirthDateValidationError}
            onChange={updateChildBirthDate}
            disabled={verified_dob}
            shouldDisplayTooltip={verified_dob}
          />
        </div>

        <div className="w-full space-y-4 pb-5 md:w-auto">
          <Button
            type="submit"
            ctaText={confirmCta}
            disabled={processingButton !== ''}
            includeSpinner={processingButton === 'confirm'}
            fullWidth
          />

          <Button
            ctaText={cancelCta}
            style={BUTTON_STYLES.secondary}
            onClick={() => {
              setProcessingButton('cancel');
              back();
            }}
            disabled={processingButton !== ''}
            includeSpinner={processingButton === 'cancel'}
            fullWidth
          />
        </div>
      </form>
    </div>
  );
};

export default React.memo(EditChildDetailsForm);
